// ============================================================================
// FSA ENGAGEMENT SETTINGS HOOK
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fsaEngagementService } from 'api/services/fsa/fsaEngagementService';
import type { ID, PaginatedResponse } from 'api/types';
import type {
  FsaEngagementSettings,
  FsaEngagementListSearchRequest,
} from 'api/types/fsa/engagementSettings.types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Paged list of engagements that have FSA configured. Powers the
 * "FSA Engagements" sidebar screen.
 */
export const useFsaEngagements = (params?: FsaEngagementListSearchRequest) => {
  const [data, setData] = useState<FsaEngagementSettings[]>([]);
  const [pagination, setPagination] =
    useState<PaginatedResponse<FsaEngagementSettings>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchEngagements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fsaEngagementService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchEngagements();
  }, [fetchEngagements]);

  return { data, pagination, loading, error, refetch: fetchEngagements };
};

/**
 * Fetches FSA settings for an engagement. `data` is null while loading
 * OR when no settings have been configured yet for the engagement (the
 * UI uses null → render "Configure FSA" CTA).
 */
export const useFsaEngagementSettings = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<FsaEngagementSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fsaEngagementService.getSettings(engagementId);
      setData(response.data ?? null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
};
